import React, { useState } from "react";
import axios from "axios";
import { Box, Typography, Button, IconButton } from "@mui/material";
import AttachmentPreviewDialog from "./AttachmentPreviewDialog.jsx";
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import MovieIcon from '@mui/icons-material/Movie';

const s3Axios = axios.create({
  withCredentials: false,
  headers: {}
});

export const removeTimestampPrefix = (fileName) => {
  if (!fileName) return "file";
  return fileName.replace(/^\d+-/, '');
};

export const forceDownloadAxios = async (url, fileName) => {
  try {
    const cleanFileName = removeTimestampPrefix(fileName);
    const res = await s3Axios.get(url, {
      responseType: "blob",
      params: { t: Date.now() }
    });
    const blobUrl = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = cleanFileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Download failed:", error);
  }
};

const FilePreview_ = React.memo(function FilePreview({ file, url, fileName, msg, onViewFile }) {
  const [previewOpen, setPreviewOpen] = useState(false);

  if (!file || !url) return null;
  const displayName = removeTimestampPrefix(fileName);

  const ActionContainer = () => (
    <Box sx={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
      <Button
        variant="contained"
        size="small"
        startIcon={<DownloadIcon sx={{ fontSize: '14px !important' }} />}
        onClick={(e) => { e.stopPropagation(); forceDownloadAxios(url, fileName); }}
        sx={{
          background: '#7DD3FC',
          textTransform: 'none',
          fontSize: '11px',
          fontWeight: 600,
          borderRadius: '6px',
          padding: '4px 12px',
          boxShadow: 'none',
          '&:hover': { background: '#38BDF8', boxShadow: 'none' }
        }}
      >
        Download
      </Button>
      <Button
        variant="outlined"
        size="small"
        startIcon={<VisibilityIcon sx={{ fontSize: '14px !important' }} />}
        onClick={(e) => { 
          e.stopPropagation(); 
          if (onViewFile) {
            onViewFile(url, fileName, file);
          } else {
            setPreviewOpen(true);
          }
        }}
        sx={{
          borderColor: '#E2E8F0',
          color: '#475569',
          textTransform: 'none',
          fontSize: '11px',
          fontWeight: 600,
          borderRadius: '6px',
          padding: '4px 12px',
          '&:hover': { borderColor: '#7DD3FC', background: 'rgba(125, 211, 252, 0.05)' }
        }}
      >
        View
      </Button>
    </Box>
  );

  const renderFileIcon = () => {
    if (file.startsWith("audio/")) return <AudiotrackIcon sx={{ color: '#7DD3FC' }} />;
    if (file.startsWith("video/")) return <MovieIcon sx={{ color: '#7DD3FC' }} />;
    if (file === "application/pdf") return <PictureAsPdfIcon sx={{ color: '#F43F5E' }} />;
    return <InsertDriveFileIcon sx={{ color: '#64748B' }} />;
  };

  return (
    <Box 
      sx={{ 
        border: '1px solid #E2E8F0',
        borderRadius: '12px',
        padding: '16px',
        maxWidth: '350px',
        background: '#F8FAFC',
        transition: 'all 0.2s ease',
        '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }
      }}
    >
      {/* Media Content */}
      {file.startsWith("image/") ? (
        <img 
          src={url} 
          alt={displayName} 
          style={{ width: "100%", borderRadius: "8px", marginBottom: "12px", border: '1px solid #E2E8F0' }} 
        />
      ) : file.startsWith("audio/") ? (
        <Box sx={{ marginBottom: '12px' }}>
           <audio controls src={url} style={{ width: "100%" }} />
        </Box>
      ) : file.startsWith("video/") ? (
        <video controls width="100%" src={url} style={{ borderRadius: "8px", marginBottom: "12px" }} />
      ) : null}

      {/* File Info */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        {renderFileIcon()}
        <Box sx={{ minWidth: 0 }}>
          <Typography 
            sx={{ 
              fontSize: '13px', 
              fontWeight: 600, 
              color: '#0F172A', 
              whiteSpace: 'nowrap', 
              overflow: 'hidden', 
              textOverflow: 'ellipsis',
              fontFamily: "'Inter', sans-serif"
            }}
          >
            {displayName}
          </Typography>
          <Typography sx={{ fontSize: '11px', color: '#64748B', wordBreak: 'break-all' }}>
            {file.split('/')[1]?.toUpperCase() || 'FILE'}
          </Typography>
        </Box>
      </Box>

      <ActionContainer />
      <AttachmentPreviewDialog 
        open={previewOpen} 
        onClose={() => setPreviewOpen(false)} 
        file={{ url, originalName: fileName, type: file }} 
      />
    </Box>
  );
});

export default FilePreview_;
